<template>
<div v-show="!isEmpty(configData)">
  <el-row type="flex" class="title-bar" justify="space-between">
    <div>
      <span class="tit content-title">구성 정보 변경 이력</span>
      <span class="condition-text">({{ configPeriodLabel }})</span>
      <span v-if="isTrue(configData.isAbnormal)" class="abnormal-message">
        {{ configData.abnormalMessage }}
      </span>
    </div>
    <w-header-search-button size="mini" @click="showSearchTool = !showSearchTool"></w-header-search-button>
  </el-row>
  <!-- 구성 정보 변경 (서버, 네트워크장비, dbms, vms)-->
  <el-table-wrapper :data="configData.etcData" :column-default="columnDefault" :columns="changeHistColumns" :pagination="false"
  :show-custom-header="true" :show-search-tool="showSearchTool">
    <template slot="detail-info" slot-scope="scope">
     <span @click="onClickDetailButton(scope.row)"><i class="far fa-search-plus c-pointer" /></span>
    </template>
  </el-table-wrapper>
  <w-detail-change-hist-popup ref="detailChangeHistPopup" :selectHistParams = "selectHistParams"></w-detail-change-hist-popup>
  <w-dbms-change-hist-popup ref="dbmsChangeHistPopup" :selectHistParams = "selectHistParams"></w-dbms-change-hist-popup>
  <w-vms-change-hist-popup ref="vmsChangeHistPopup" :selectHistParams = "selectHistParams"></w-vms-change-hist-popup>
  <w-ap-controller-change-hist-popup ref="apControllerChangeHistPopup" :selectHistParams="selectHistParams"></w-ap-controller-change-hist-popup>
</div>
</template>

<script>
import moment from 'moment';
import { isNil, isEmpty, eq } from 'lodash';
import { columnDefault } from '@/constants/table';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import { isTrue } from '@/utils/boolean';
import WDetailChangeHistPopup from '@/views/config/node/components/DetailChangeHistPopup';
import WDbmsChangeHistPopup from '@/views/config/node/components/DbmsChangeHistPopup';
import WVmsChangeHistPopup from '@/views/config/node/components/VmsChangeHistPopup';
import WApControllerChangeHistPopup from '@/views/config/node/components/ApControllerChangeHistPopup';
import { etcInfoDefine } from '../../../constants/fault-analysis';
import { getConfigPeriodCondition, getPeriodConditionLabel } from '../../../utils/fault-analysis';

const getTableValueAfterCheckEmpty = (row, column, value) => getValueAfterCheckEmpty(value);
const getDateFormatter = (row, column, value) => (isNil(value) ? EMPTY_TEXT : getDateToString(value));

export default {
  name: 'WConfigChangeHistView',
  components: {
    WDetailChangeHistPopup,
    WDbmsChangeHistPopup,
    WVmsChangeHistPopup,
    WApControllerChangeHistPopup,
  },
  props: {
    /**
     * 기타 분석 정보 조회 시 필요한 정보 객체 (동시성 문제 고려하여 하나의 객체로 전달)
     * 1) obj: 분석 대상 장비 정보
     * 2) eventHistData: 분석 이벤트 이력 정보
     * 3) etcInfo: 기타 분석 정보
     * @type {Object}
     */
    data: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      columnDefault,
      showSearchTool: false,
      selectHistParams: {},
      configData: {},
      changeHistColumns: [
        {
          prop: 'typeName',
          label: '변경타입',
          searchable: true,
          sortable: true,
          formatter: getTableValueAfterCheckEmpty,
        },
        {
          prop: 'chngGrpMessage',
          label: '변경 메시지',
          searchable: true,
          sortable: true,
          formatter: getTableValueAfterCheckEmpty,
        },
        {
          prop: 'checkDatetime',
          label: '변경감지시각',
          searchable: true,
          sortable: true,
          width: 300,
          formatter: getDateFormatter,
        },
        {
          prop: 'detailHist',
          label: '상세보기',
          width: 100,
          scopedSlot: 'detail-info',
        },
      ],
    };
  },
  computed: {
    configPeriodLabel() {
      if (isEmpty(this.data) || isEmpty(this.data.eventHistData)) {
        return EMPTY_TEXT;
      }
      const period = getConfigPeriodCondition(this.data.eventHistData.eventCheckDatetime);
      return getPeriodConditionLabel(period.startDatetime, period.endDatetime);
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(to) {
        this.reset();
        if (!isNil(to)) {
          this.loadData();
        }
      },
    },
  },
  methods: {
    isEmpty,
    isTrue,
    /**
     * etcInfo에 전달된 구성 정보 변경 데이터 바인딩
     * @author jeee12
     */
    loadData() {
      if (!isEmpty(this.data.etcInfo) && !isEmpty(this.data.etcInfo[etcInfoDefine.CONFIG_CHANGE_HIST])) {
        this.configData = this.data.etcInfo[etcInfoDefine.CONFIG_CHANGE_HIST];
      } else {
        this.configData = {};
      }
    },
    /**
     * 화면을 초기화한다.
     * @author Ju
     */
    reset() {
      // 구성 정보 변경
      this.configData = {};
    },
    onClickDetailButton(record) {
      const startDatetime = moment(this.data.eventHistData.checkDatetime).clone().subtract(1, 'hour');
      const endDatetime = moment(this.data.eventHistData.checkDatetime).clone().add(1, 'hour');
      const obj = this.data.obj;
      this.selectHistParams = {
        typeKey: record.typeKey,
        typeName: record.typeName,
        objId: obj.objId,
        objDefineId: obj.objDefineId,
        chngGrpSeqNumber: record.chngGrpSeqNumber,
        checkDatetime: record.checkDatetime,
        dateRange: [startDatetime.toDate(), endDatetime.toDate()],
      };
      const objDefineId = obj.objDefineId;
      if (eq(objDefineId, 'DBMS')) {
        // DBMS 전용 팝업을 띄워야하는 경우
        this.$refs.dbmsChangeHistPopup.open();
      } else if (eq(objDefineId, 'VMS_OBJECT')) {
        this.$refs.vmsChangeHistPopup.open();
      } else if (obj.objtypeId === 'AP_CONTROLLER' && record.typeKey === 'WIRELESS_AP_CONFIG') {
        // AP 컨트롤러 전용, 무선AP 비교 이력 팝업을 띄워야 하는 경우
        this.$refs.apControllerChangeHistPopup.open();
      } else {
        this.$refs.detailChangeHistPopup.open();
      }
    },
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/popup/FaultAnalysisPopup/components/FaultAnalysisInfoView/EtcInfo/ConfigChangeHistView.vue